import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { HttpUtils } from '../../../shared/http-utils';
import { URL_BASE } from '../../../core/constants';
import { map } from 'rxjs/operators';
import {
  TruckJourneyAssignmentLoad,
  TruckJourneyDetail,
  TruckJourneyInfo,
  TruckJourneyLoadSearchCriteria,
  TruckJourneyRequest,
  TruckJourneySearchCriteria,
  TruckJourneysReorderRequest
} from '../models/truck-journey.model';
import { CarrierSync } from '../models/carrier-sync.model';

const TRUCK_JOURNEY_URL = '/api/truck-journeys';

@Injectable()
export class TruckJourneyService {

  constructor(private http: HttpClient) {}

  public searchTruckJourneys(searchCriterias: TruckJourneySearchCriteria): Observable<TruckJourneyInfo[]> {
    const headers = HttpUtils.createHeaders();
    const criteriaCopy = TruckJourneySearchCriteria.toDto(searchCriterias);
    for (const key of Object.keys(searchCriterias)) {
      if (criteriaCopy[key] === null || criteriaCopy[key] === undefined || criteriaCopy[key] === '') {
        delete criteriaCopy[key];
      }
    }
    const params: any = {...criteriaCopy}
    return this.http.get<TruckJourneyInfo[]>(`${URL_BASE}${TRUCK_JOURNEY_URL}`, { headers, params })
      .pipe(
        map((result: TruckJourneyInfo[]) => result.map(l => TruckJourneyInfo.fromDto(l)))
      );
  }

  public searchLoads(searchCriterias: TruckJourneyLoadSearchCriteria): Observable<TruckJourneyAssignmentLoad[]> {
    const headers = HttpUtils.createHeaders();
    const criteriaCopy = TruckJourneyLoadSearchCriteria.toDto(searchCriterias);
    for (const key of Object.keys(searchCriterias)) {
      if (criteriaCopy[key] === null || criteriaCopy[key] === undefined || criteriaCopy[key] === '') {
        delete criteriaCopy[key];
      }
    }
    const params: any = {...criteriaCopy}
    return this.http.get<TruckJourneyAssignmentLoad[]>(`${URL_BASE}${TRUCK_JOURNEY_URL}/loads`, { headers, params })
      .pipe(
        map((result: TruckJourneyAssignmentLoad[]) => result.map(res => TruckJourneyAssignmentLoad.fromDto(res)))
      );
  }

  public getTruckJourney(truckJourneyId: string): Observable<TruckJourneyDetail> {
    const headers = HttpUtils.createHeaders();
    return this.http.get<TruckJourneyDetail>(`${URL_BASE}${TRUCK_JOURNEY_URL}/${truckJourneyId}`, { headers })
      .pipe(
        map(result => TruckJourneyDetail.fromDto(result))
      );
  }

  public createTruckJourney(truckJourney: TruckJourneyRequest): Observable<string> {
    const headers = HttpUtils.createHeaders();
    return this.http.post<string>(
      `${URL_BASE}${TRUCK_JOURNEY_URL}`,
      TruckJourneyRequest.toDto(truckJourney),
      {
        headers,
        responseType: 'text' as 'json'
      }
    );
  }

  public updateTruckJourney(truckJourney: TruckJourneyRequest): Observable<TruckJourneyDetail> {
    const headers = HttpUtils.createHeaders();
    const url = `${URL_BASE}${TRUCK_JOURNEY_URL}/${truckJourney.id}`;
    return this.http.put(url, TruckJourneyRequest.toDto(truckJourney), { headers })
      .pipe(
        map(result => TruckJourneyDetail.fromDto(result))
      );
  }

  public deleteTruckJourney(truckJourneyId: string): Observable<void> {
    const headers = HttpUtils.createHeaders();
    return this.http.delete<void>(`${URL_BASE}${TRUCK_JOURNEY_URL}/${truckJourneyId}`, { headers });
  }

  public reorderTruckJourneys(truckJourneysReorderRequest: TruckJourneysReorderRequest): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const url = `${URL_BASE}${TRUCK_JOURNEY_URL}/reorder`;
    return this.http.put<void>(url, truckJourneysReorderRequest, { headers });
  }

  public synchronizeCarrier(carrierId: string): Observable<string> {
    const headers = HttpUtils.createHeaders();
    return this.http.put<string>(`${URL_BASE}${TRUCK_JOURNEY_URL}/${carrierId}/sync`, null, { headers });
  }

  public getCarrierLastSyncResult(carrierId: string): Observable<CarrierSync> {
    const headers = HttpUtils.createHeaders();
    return this.http.get<CarrierSync>(`${URL_BASE}${TRUCK_JOURNEY_URL}/${carrierId}/sync`, { headers })
      .pipe(map((result: any) => CarrierSync.fromDto(result)));
  }
}
